'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAppStore } from '@/store/app';
import { translate } from '@/lib/i18n';
import { formatPrice, formatVolume, formatCap, formatPercent } from '@/lib/format';
import DataQualityBadge from '@/components/research/DataQualityBadge';
import MetricInfoPopover from '@/components/research/MetricInfoPopover';
import CoinLogo from '@/components/CoinLogo';
import type { MarketCoin } from '@glintfans/shared';

/* ─────────────────────────── 类型 ─────────────────────────── */

interface SeriesTrack {
  metricId: string;
  unit: string;
  points: Array<{ t: number; v: number }>;
  quality: 'ok' | 'partial' | 'stale' | 'missing' | 'unsupported';
  coverage: { expectedCount: number; validCount: number; ratio: number; excludedReasons: string[] };
  asOf: number;
}

interface SeriesResponse {
  instrumentId: string;
  range: string;
  interval: string;
  asOf: number;
  windowStart: number;
  windowEnd: number;
  expectedCount: number;
  tracks: SeriesTrack[];
}

interface LayoutDto {
  id: string;
  name: string;
  instrumentId: string;
  range: string;
  interval: string;
  tracks: string[];
  sourceScope: Record<string, unknown> | null;
  timezone: string;
  version: number;
  shareToken?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Percentile {
  current: number | null;
  percentile: number | null;
  min: number | null;
  max: number | null;
  median: number | null;
  samples: number;
  expected: number;
  coverage: number;
  quality: string;
  windowStart?: number;
  windowEnd?: number;
  asOf?: number;
}

const VENUES = [
  { id: 'binance', label: 'Binance' },
  { id: 'okx', label: 'OKX' },
  { id: 'gate', label: 'Gate' },
  { id: 'bitget', label: 'Bitget' },
];

const RANGES = ['24h', '7d', '30d', '90d'];
const INTERVALS = ['1m', '5m', '15m', '1h', '4h', '1d'];

const ALL_TRACKS = [
  { id: 'price', label: 'price', unit: 'price' },
  { id: 'quote_volume', label: 'quote_volume', unit: 'usd' },
  { id: 'open_interest', label: 'open_interest', unit: 'usd' },
  { id: 'funding_rate', label: 'funding_rate', unit: 'rate' },
  { id: 'liquidation', label: 'liquidation', unit: 'usd' },
];

/** D12-fix: human-readable track names (no raw metricId leakage). */
const TRACK_LABELS: Record<string, { zh: string; en: string }> = {
  price: { zh: '价格', en: 'Price' },
  quote_volume: { zh: '成交量', en: 'Volume' },
  open_interest: { zh: '持仓', en: 'Open Interest' },
  funding_rate: { zh: '资金费率', en: 'Funding Rate' },
  liquidation: { zh: '爆仓', en: 'Liquidation' },
};
const trackLabel = (id: string, zh: boolean) => TRACK_LABELS[id]?.[zh ? 'zh' : 'en'] ?? id;

/** D12-fix: quality status in human-readable form (ok/partial/missing → 正常/部分/缺失). */
const qualityLabel = (q: string, zh: boolean): string => {
  if (zh) return q === 'ok' ? '完整' : q === 'partial' ? '部分' : q === 'missing' ? '缺失' : q === 'unsupported' ? '不支持' : q;
  return q === 'ok' ? 'complete' : q === 'partial' ? 'partial' : q === 'missing' ? 'missing' : q === 'unsupported' ? 'unsupported' : q;
};

/** D12: track colors use theme tokens wrapped in rgb() (tokens are bare RGB triplets). */
const TRACK_COLORS: Record<string, string> = {
  price: 'rgb(var(--chart-price))',
  quote_volume: 'rgb(var(--chart-volume))',
  open_interest: 'rgb(var(--chart-oi))',
  funding_rate: 'rgb(var(--chart-funding))',
  liquidation: 'rgb(var(--gf-negative))',
};

const fmtVal = (metricId: string, unit: string, v: number): string => {
  if (metricId === 'price') return formatPrice(v);
  if (metricId === 'funding_rate') return `${(v * 100).toFixed(4)}%`;
  if (metricId === 'open_interest') return formatCap(v);
  return formatVolume(v);
};

/* ───────────────────── 联动图表组件 ───────────────────── */

function LinkedChart({
  tracks,
  height,
  subHeight,
  windowStart,
  windowEnd,
  expectedCount,
}: {
  tracks: SeriesTrack[];
  height: number;
  subHeight: number;
  windowStart?: number;
  windowEnd?: number;
  expectedCount?: number;
}) {
  const { language } = useAppStore();
  const zh = language !== 'en';
  const wrapRef = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(720);

  // D06: 视图时间域 = series 返回的明确窗口 [windowStart, windowEnd)（UTC）。
  // 数据点只用于 fallback；缺口轨道不拉满时间轴。
  const all = useMemo(() => {
    const pts: Array<{ t: number; v: number }> = [];
    tracks.forEach((tr) => pts.push(...tr.points));
    if (!pts.length) return null;
    let tMin = Infinity, tMax = -Infinity;
    pts.forEach((p) => { if (p.t < tMin) tMin = p.t; if (p.t > tMax) tMax = p.t; });
    return { tMin, tMax };
  }, [tracks]);

  const domain = useMemo(() => {
    if (windowStart != null && windowEnd != null && windowEnd > windowStart) return { a: windowStart, b: windowEnd };
    if (all) return { a: all.tMin, b: Math.max(all.tMin + 1, all.tMax) };
    return null;
  }, [windowStart, windowEnd, all]);

  const [view, setView] = useState<{ a: number; b: number } | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [drag, setDrag] = useState<{ x: number; viewA: number; viewB: number } | null>(null);

  // 容器宽度
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.max(320, el.clientWidth)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // D06: 窗口/数据变化 → fitContent 只作用于本窗口 domain；缩放上限 = 窗口。
  useEffect(() => {
    if (!domain) { setView(null); return; }
    setView({ a: domain.a, b: domain.b });
    setHover(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain?.a, domain?.b]);

  const onWheel = useCallback((e: React.WheelEvent) => {
    if (!view || !domain) return;
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const frac = (e.clientX - rect.left) / rect.width;
    const span = view.b - view.a;
    const factor = e.deltaY > 0 ? 1.25 : 0.8;
    const newSpan = Math.min(span * factor, domain.b - domain.a);
    const anchor = view.a + span * frac;
    const a = anchor - newSpan * frac;
    const b = a + newSpan;
    setView({ a, b });
    setHover(null);
  }, [view, domain]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (!view) return;
    setDrag({ x: e.clientX, viewA: view.a, viewB: view.b });
  }, [view]);

  useEffect(() => {
    if (!drag || !domain) return;
    const move = (ev: MouseEvent) => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect) return;
      const dx = ev.clientX - drag.x;
      const pxPerMs = rect.width / (drag.viewB - drag.viewA);
      const dt = dx / pxPerMs;
      const span = drag.viewB - drag.viewA;
      let a = drag.viewA - dt;
      if (a < domain.a) a = domain.a;
      let b = a + span;
      if (b > domain.b) { b = domain.b; a = b - span; }
      setView({ a, b });
      setHover(null);
    };
    const up = () => setDrag(null);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [drag, domain]);

  const onMove = useCallback((e: React.MouseEvent) => {
    if (!view || drag) return;
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    setHover(view.a + (view.b - view.a) * frac);
  }, [view, drag]);

  // 可见点
  const visible = useMemo(() => {
    if (!view) return null;
    const out: Record<string, Array<{ t: number; v: number }>> = {};
    tracks.forEach((tr) => {
      out[tr.metricId] = tr.points.filter((p) => p.t >= view.a && p.t <= view.b);
    });
    return out;
  }, [tracks, view]);

  // 刻度标签
  const ticks = useMemo(() => {
    if (!view) return [];
    const n = 5;
    const out: number[] = [];
    for (let i = 0; i < n; i++) out.push(view.a + ((view.b - view.a) * i) / (n - 1));
    return out;
  }, [view]);

  // hover 时刻各轨道最近值（D12: 含质量状态，供 tooltip 展示「时间/指标/值/单位/状态」）
  const hoverVals = useMemo(() => {
    if (hover == null || !tracks.length) return null;
    const out: Array<{ metricId: string; v: number | null; unit: string; quality: SeriesTrack['quality'] }> = [];
    tracks.forEach((tr) => {
      if (tr.metricId === 'price' || tr.metricId === 'quote_volume') return;
      let best: { t: number; v: number } | null = null;
      for (const p of tr.points) {
        if (!best || Math.abs(p.t - hover) < Math.abs(best.t - hover)) best = p;
      }
      out.push({ metricId: tr.metricId, v: best ? best.v : null, unit: tr.unit, quality: tr.quality });
    });
    return out;
  }, [hover, tracks]);

  if (!domain || !view) {
    return <div style={{ height }} className="flex items-center justify-center text-xs text-gf-muted">{zh ? '暂无数据' : 'No data'}</div>;
  }

  const x = (t: number) => ((t - view.a) / (view.b - view.a)) * w;

  // 主图：价格
  const priceTrack = tracks.find((tr) => tr.metricId === 'price');
  const volTrack = tracks.find((tr) => tr.metricId === 'quote_volume');
  const pricePts = priceTrack ? priceTrack.points.filter((p) => p.t >= view.a && p.t <= view.b) : [];
  const volPts = volTrack ? volTrack.points.filter((p) => p.t >= view.a && p.t <= view.b) : [];
  const priceMin = pricePts.length ? Math.min(...pricePts.map((p) => p.v)) : 0;
  const priceMax = pricePts.length ? Math.max(...pricePts.map((p) => p.v)) : 1;
  const priceSpan = priceMax - priceMin || 1;
  const volMax = volPts.length ? Math.max(...volPts.map((p) => p.v)) : 1;
  const y = (v: number) => 10 + (1 - (v - priceMin) / priceSpan) * (height - 30);
  const priceLine = pricePts.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
  const hoverX = hover != null ? x(hover) : null;

  // 副图（依次竖排）
  const subTracks = tracks.filter((tr) => tr.metricId !== 'price' && tr.metricId !== 'quote_volume');

  return (
    <div
      ref={wrapRef}
      className="relative w-full select-none overflow-hidden rounded-lg border border-gf-border/60 bg-gf-bg/40"
      style={{ cursor: drag ? 'grabbing' : 'grab' }}
      onWheel={onWheel}
      onMouseDown={onMouseDown}
      onMouseMove={onMove}
      onMouseLeave={() => { setHover(null); }}
    >
      <svg width={w} height={height} className="block">
        {/* 网格 + 价格线 (D12: grid uses chart token) */}
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1={0} x2={w} y1={height * f} y2={height * f} style={{ stroke: 'rgb(var(--chart-grid))' }} strokeOpacity="0.6" strokeDasharray="3 3" />
        ))}
        {pricePts.length > 1 && (
          <>
            <path d={priceLine} fill="none" style={{ stroke: TRACK_COLORS.price }} strokeWidth="2" vectorEffect="non-scaling-stroke" />
            <path
              d={`${priceLine} L${x(pricePts[pricePts.length - 1].t).toFixed(1)},${height} L${x(pricePts[0].t).toFixed(1)},${height} Z`}
              style={{ fill: TRACK_COLORS.price }}
              opacity="0.08"
            />
          </>
        )}
        {/* 成交量柱（主图底部） */}
        {volPts.map((p, i) => (
          <rect
            key={i}
            x={x(p.t) - Math.max(1, w / volPts.length / 2.4)}
            y={height - (p.v / volMax) * height * 0.28}
            width={Math.max(1, w / volPts.length / 1.2)}
            height={(p.v / volMax) * height * 0.28}
            style={{ fill: TRACK_COLORS.quote_volume }}
            opacity="0.45"
          />
        ))}
        {/* 十字光标 */}
        {hover != null && hoverX != null && (
          <>
            <line x1={hoverX} x2={hoverX} y1={0} y2={height} style={{ stroke: 'rgb(var(--chart-crosshair))' }} strokeOpacity="0.6" strokeDasharray="2 3" />
            <rect x={Math.min(w - 130, Math.max(2, hoverX - 60))} y={4} width={124} height={15} rx={3} style={{ fill: 'rgb(var(--gf-card))', stroke: 'rgb(var(--gf-border))' }} />
            <text x={Math.min(w - 130, Math.max(2, hoverX - 60)) + 62} y={15} fontSize="13" textAnchor="middle" style={{ fill: 'rgb(var(--chart-text))' }}>
              {new Date(hover).toLocaleString(zh ? 'zh-TW' : 'en-US', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}
            </text>
          </>
        )}
        {/* 时间刻度 (D12: 桌面 12px 级别可读字号) */}
        {ticks.map((tk, i) => (
          <text key={i} x={x(tk)} y={height - 5} fontSize="11" textAnchor="middle" style={{ fill: 'rgb(var(--chart-text))' }}>
            {new Date(tk).toLocaleString(zh ? 'zh-TW' : 'en-US', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}
          </text>
        ))}
      </svg>
      {/* 副图 */}
      {subTracks.map((tr) => {
        const pts = tr.points.filter((p) => p.t >= view.a && p.t <= view.b);
        const min = pts.length ? Math.min(...pts.map((p) => p.v)) : 0;
        const max = pts.length ? Math.max(...pts.map((p) => p.v)) : 1;
        const span = max - min || 1;
        const sy = (v: number) => 8 + (1 - (v - min) / span) * (subHeight - 22);
        const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.t).toFixed(1)},${sy(p.v).toFixed(1)}`).join(' ');
        const last = pts.length ? pts[pts.length - 1].v : null;
        const hval = hoverVals?.find((h) => h.metricId === tr.metricId);
        return (
          <div key={tr.metricId} className="border-t border-gf-border/50">
            {pts.length > 1 ? (
              <svg width={w} height={subHeight} className="block">
                {[0.5].map((f) => (
                  <line key={f} x1={0} x2={w} y1={subHeight * f} y2={subHeight * f} style={{ stroke: 'rgb(var(--chart-grid))' }} strokeOpacity="0.5" strokeDasharray="3 3" />
                ))}
                <path d={line} fill="none" style={{ stroke: TRACK_COLORS[tr.metricId] ?? 'rgb(var(--chart-price))' }} strokeWidth="1.75" vectorEffect="non-scaling-stroke" />
                <path
                  d={`${line} L${x(pts[pts.length - 1].t).toFixed(1)},${subHeight} L${x(pts[0].t).toFixed(1)},${subHeight} Z`}
                  style={{ fill: TRACK_COLORS[tr.metricId] ?? 'rgb(var(--chart-price))' }}
                  opacity="0.08"
                />
                {hoverX != null && (
                  <line x1={hoverX} x2={hoverX} y1={0} y2={subHeight} style={{ stroke: 'rgb(var(--chart-crosshair))' }} strokeOpacity="0.6" strokeDasharray="2 3" />
                )}
                <text x={4} y={11} fontSize="11" style={{ fill: 'rgb(var(--chart-text))' }}>
                  {trackLabel(tr.metricId, zh)} {last != null ? fmtVal(tr.metricId, tr.unit, last) : '—'}
                  {hval?.v != null ? ` · ${fmtVal(tr.metricId, tr.unit, hval.v)}` : ''}
                </text>
              </svg>
            ) : (
              // D12: 空轨道显示原因，不留没有解释的大黑框
              <div className="flex items-center gap-1 px-2 py-1 text-[11px] text-gf-muted">
                <span style={{ color: TRACK_COLORS[tr.metricId] ?? 'rgb(var(--chart-price))' }}>{trackLabel(tr.metricId, zh)}</span>
                <span className="text-gf-muted">
                  {tr.quality === 'missing' || tr.quality === 'unsupported'
                    ? (zh ? `暂无数据（${tr.quality}）` : `No data (${tr.quality})`)
                    : (zh ? '暂无可比较数据' : 'No comparable data')}
                  {tr.coverage?.excludedReasons?.length ? `：${tr.coverage.excludedReasons.slice(0, 2).join('；')}` : ''}
                  {tr.asOf ? ` · ${new Date(tr.asOf).toLocaleString(zh ? 'zh-TW' : 'en-US', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}` : ''}
                </span>
              </div>
            )}
          </div>
        );
      })}
      {/* 顶部主图标签 (D12: 12px 可读) */}
      <div className="pointer-events-none absolute left-2 top-1.5 flex items-center gap-2">
        <span className="text-xs font-semibold" style={{ color: TRACK_COLORS.price }}>
          {priceTrack ? `${fmtVal('price', 'price', pricePts.length ? pricePts[pricePts.length - 1].v : 0)}` : '—'}
        </span>
        {priceTrack && (
          <DataQualityBadge quality={priceTrack.quality} asOf={priceTrack.asOf} />
        )}
      </div>
      {/* hover tooltip：时间 + 各轨道指标/值/单位/状态 (D12: ≥13px) */}
      {hover != null && hoverVals && (
        <div className="pointer-events-none absolute left-2 top-6 z-10 rounded border border-gf-border bg-gf-card/95 px-2 py-1 text-[13px] leading-5 shadow">
          <div className="font-medium text-gf-text">
            {new Date(hover).toLocaleString(zh ? 'zh-TW' : 'en-US', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}
          </div>
          {hoverVals.map((h) => (
            <div key={h.metricId} className="flex items-center gap-1.5 text-gf-muted">
              <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: TRACK_COLORS[h.metricId] ?? 'rgb(var(--chart-price))' }} />
              <span>{trackLabel(h.metricId, zh)}</span>
              <span className="font-mono text-gf-text">{h.v != null ? fmtVal(h.metricId, h.unit, h.v) : '—'}</span>
              <span className="text-gf-muted/70">· {qualityLabel(h.quality, zh)}</span>
            </div>
          ))}
        </div>
      )}
      {/* 提示：缩放 / 平移 */}
      <div className="pointer-events-none absolute right-2 top-1.5 text-[10px] text-gf-muted/70">
        {zh ? '滚轮缩放 · 拖拽平移' : 'Scroll to zoom · Drag to pan'}
      </div>
    </div>
  );
}

/* ─────────────────────────── 币种联想搜索 ─────────────────────────── */

interface SymbolSearchResult {
  coins: MarketCoin[];
  pairs: Array<{ symbol: string; baseAsset: string; quoteAsset?: string }>;
}

/** 联动分析页的币对选择：关键词联想搜索（复用全局 /market/search）。 */
function SymbolPicker(props: {
  value: string;
  zh: boolean;
  onPick: (s: { symbol: string; exchange?: string; type?: 'spot' | 'perp'; baseAsset?: string; quoteAsset?: string }) => void;
}) {
  const { value, zh, onPick } = props;
  const [inputVal, setInputVal] = useState(value);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // 搜索输入防抖 250ms
  const debouncedQ = useMemo(() => q.trim(), [q]);
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(debouncedQ), 250);
    return () => clearTimeout(t);
  }, [debouncedQ]);

  const { data, isFetching } = useQuery({
    queryKey: ['chart-symbol-search', debounced],
    queryFn: () => api.get<SymbolSearchResult>(`/market/search?q=${encodeURIComponent(debounced)}`),
    enabled: debounced.length >= 1,
    staleTime: 2_000,
  });

  // 外部点击关闭
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
        setInputVal(value);
        setQ('');
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [value]);

  // 无报价（price 为空）的币对不展示，避免用户点进无数据标的
  const coins = (data?.coins ?? []).filter((c) => c.price != null);

  const pick = (c: MarketCoin) => {
    onPick({ symbol: c.symbol, exchange: c.exchange, type: c.type === 'spot' ? 'spot' : 'perp', baseAsset: c.baseAsset, quoteAsset: c.quoteAsset });
    setInputVal(c.symbol);
    setOpen(false);
    setQ('');
  };

  return (
    <div ref={boxRef} className="relative">
      <div className="flex items-center gap-1.5 rounded border border-gf-border bg-gf-surface2 px-2 py-1">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-gf-muted">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <input
          value={open ? q : inputVal}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => { setOpen(true); setQ(''); }}
          placeholder={zh ? '搜索币种 / 交易对…' : 'Search coins / symbols…'}
          className="w-32 bg-transparent font-mono text-xs outline-none placeholder:text-gf-muted sm:w-40"
        />
        {isFetching && <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-gf-accent" />}
      </div>

      {open && q.trim() && (
        <div className="absolute left-0 top-full z-50 mt-1 max-h-80 w-[340px] max-w-[80vw] overflow-auto rounded-md border border-gf-border bg-gf-surface shadow-xl">
          {coins.length === 0 && !isFetching ? (
            <div className="px-3 py-4 text-center text-xs text-gf-muted">
              {zh ? `没有找到「${q}」的结果` : `No results for "${q}"`}
            </div>
          ) : (
            coins.map((c) => {
              const isSpot = c.type === 'spot';
              const pct = c.priceChangePercent24h ?? 0;
              return (
                <button
                  key={`${c.exchange}-${c.symbol}-${isSpot ? 's' : 'f'}`}
                  type="button"
                  onClick={() => pick(c)}
                  className="flex w-full items-center gap-2 border-b border-gf-border px-3 py-2 text-left hover:bg-gf-surface2"
                >
                  <CoinLogo symbol={c.symbol} size={26} />
                  <span className="min-w-0 flex-1 leading-tight">
                    <span className="flex items-center gap-1">
                      <span className="font-medium">{c.baseAsset}</span>
                      <span className="text-xs text-gf-muted">/ {c.quoteAsset}</span>
                    </span>
                    <span className="mt-0.5 flex items-center gap-1 text-[10px] text-gf-muted">
                      <span className={`rounded px-1 py-px ${isSpot ? 'bg-gf-surface2 text-gf-muted' : 'bg-gf-accent/10 text-gf-accent'}`}>
                        {isSpot ? (zh ? '现货' : 'Spot') : (zh ? '永续合约' : 'Perp')}
                      </span>
                      <span>{c.exchange || 'Binance'}</span>
                    </span>
                  </span>
                  <span className="ml-auto flex shrink-0 flex-col items-end">
                    <span className="font-mono text-xs">{formatPrice(c.price)}</span>
                    <span className={`font-mono text-xs ${pct >= 0 ? 'text-gf-positive' : 'text-gf-negative'}`}>{formatPercent(pct)}</span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── 页面 ─────────────────────────── */

export default function ChartPage() {
  const { language, user } = useAppStore();
  const t = translate(language);
  const zh = language !== 'en';

  // 默认 BTC 永续
  const [symbol, setSymbol] = useState('BTCUSDT');
  const [quote, setQuote] = useState('USDT');
  const [venue, setVenue] = useState('binance');
  const [product, setProduct] = useState<'spot' | 'perp'>('perp');
  const [range, setRange] = useState('7d');
  const [interval, setInterval] = useState('1h');
  const [tracks, setTracks] = useState<SeriesTrack[] | null>(null);
  // D06: series 窗口元数据（UTC 起止 + expected），驱动图表 domain 与信息弹层。
  const [seriesMeta, setSeriesMeta] = useState<{ windowStart: number; windowEnd: number; expectedCount: number; asOf: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState(false);

  // 手机副图切换
  const [mobileSub, setMobileSub] = useState<'open_interest' | 'funding_rate' | 'liquidation'>('open_interest');
  // R06: per-track on/off — price is always on (main chart); others are user-configurable.
  const [trackOn, setTrackOn] = useState<Record<string, boolean>>({
    price: true,
    quote_volume: true,
    open_interest: true,
    funding_rate: true,
    liquidation: true,
  });

  // 布局
  const [layouts, setLayouts] = useState<LayoutDto[]>([]);
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [layoutName, setLayoutName] = useState('');
  const [layoutSaved, setLayoutSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [activeLayoutId, setActiveLayoutId] = useState<string | null>(null);
  const [unsaved, setUnsaved] = useState(false);
  const [percentile, setPercentile] = useState<Percentile | null>(null);
  // D06: 分位口径详情弹层（点击开合）
  const [infoOpen, setInfoOpen] = useState(false);

  const base = symbol.slice(0, symbol.length - quote.length);
  const instrumentId = `${venue}:${product}:${base}:${quote}`;

  // 轨道白名单（按 capability：现货无合约专属）
  const availableTracks = useMemo(() => {
    const ids = ['price', 'quote_volume'];
    if (product === 'perp') ids.push('open_interest', 'funding_rate');
    ids.push('liquidation');
    return ids;
  }, [product]);

  const metricIds = useMemo(() => {
    const order = ['price', 'quote_volume', 'open_interest', 'funding_rate', 'liquidation'];
    return order
      .filter((id) => trackOn[id] && (product === 'perp' || (id !== 'open_interest' && id !== 'funding_rate')))
      .slice(0, 4);
  }, [trackOn, product]);

  const load = useCallback(async (inst: string, rng: string, iv: string, mets: string[]) => {
    setLoading(true);
    setError(false);
    // D06: 切换范围/粒度/交易所时先清除旧系列，禁止旧缓存残留。
    setTracks(null);
    setSeriesMeta(null);
    try {
      const res = await api.get<SeriesResponse>(
        `/research/series?instrumentId=${encodeURIComponent(inst)}&metricIds=${mets.join(',')}&range=${rng}&interval=${iv}`,
      );
      setTracks(res.tracks ?? []);
      setSeriesMeta({ windowStart: res.windowStart, windowEnd: res.windowEnd, expectedCount: res.expectedCount, asOf: res.asOf });
    } catch {
      setError(true);
      setTracks(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // 主加载：标记旧图“正在更新”直到新数据到达
  useEffect(() => {
    setStale(true);
    setUnsaved(true);
    const timer = setTimeout(() => {
      load(instrumentId, range, interval, metricIds).then(() => setStale(false));
    }, 30);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrumentId, range, interval, metricIds.join(',')]);

  // 分位（桌面右侧栏）
  useEffect(() => {
    let alive = true;
    fetch(
      `/api/v1/research/percentile?instrumentId=${encodeURIComponent(instrumentId)}&metricId=price&range=${range}&interval=${interval}`,
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (alive) setPercentile(j); })
      .catch(() => { if (alive) setPercentile(null); });
    return () => { alive = false; };
  }, [instrumentId, range, interval]);

  const loadLayouts = async () => {
    if (!user) return;
    try {
      const rows = await api.get<LayoutDto[]>('/research/layouts');
      setLayouts(rows);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    loadLayouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // 未保存离开提示
  useEffect(() => {
    if (!unsaved) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [unsaved]);

  const applyLayout = (l: LayoutDto) => {
    const parts = l.instrumentId.split(':');
    if (parts.length >= 4) {
      setVenue(parts[0]);
      setProduct(parts[1] as 'spot' | 'perp');
      setSymbol(`${parts[2]}${parts[3]}`);
      setQuote(parts[3]);
    }
    setRange(l.range);
    setInterval(l.interval);
    setLayoutName(l.name);
    setActiveLayoutId(l.id);
    // R06: layout tracks apply to the track switches too.
    setTrackOn({
      price: true,
      quote_volume: l.tracks?.includes('quote_volume') ?? true,
      open_interest: l.tracks?.includes('open_interest') ?? true,
      funding_rate: l.tracks?.includes('funding_rate') ?? true,
      liquidation: l.tracks?.includes('liquidation') ?? true,
    });
    setLayoutOpen(false);
    setUnsaved(false);
  };

  const saveLayout = async () => {
    if (!user) { window.location.href = '/login?redirect=/chart'; return; }
    const name = layoutName.trim();
    if (!name) return;
    if (name.length > 40) return;
    setSaving(true);
    try {
      const body = {
        name,
        instrumentId,
        range,
        interval,
        tracks: metricIds,
        sourceScope: { product, venue },
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Shanghai',
      };
      if (activeLayoutId) {
        await api.put<LayoutDto>(`/research/layouts/${activeLayoutId}`, body);
      } else {
        await api.post<LayoutDto>('/research/layouts', body);
      }
      setLayoutSaved(true);
      setUnsaved(false);
      setTimeout(() => setLayoutSaved(false), 2000);
      await loadLayouts();
    } catch { /* ignore */ } finally {
      setSaving(false);
    }
  };

  const deleteLayout = async (id: string) => {
    try {
      await api.delete(`/research/layouts/${id}`);
      setLayouts((p) => p.filter((x) => x.id !== id));
    } catch { /* ignore */ }
  };

  const shareLayout = async (id: string) => {
    try {
      const res = await api.post<{ token: string }>(`/research/layouts/${id}/share`);
      setShareUrl(`${window.location.origin}/chart?share=${res.token}`);
    } catch { /* ignore */ }
  };

  // 公共分享链接加载
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const token = sp.get('share');
    if (!token) return;
    (async () => {
      try {
        const res = await api.get<{ ok: boolean; layout: Omit<LayoutDto, 'id' | 'createdAt' | 'updatedAt' | 'shareToken'> }>(`/research/layouts/shared/${token}`);
        if (res.ok && res.layout) {
          const l = res.layout;
          const parts = l.instrumentId.split(':');
          if (parts.length >= 4) {
            setVenue(parts[0]);
            setProduct(parts[1] as 'spot' | 'perp');
            setSymbol(`${parts[2]}${parts[3]}`);
          }
          setRange(l.range);
          setInterval(l.interval);
          setLayoutName(l.name);
          setTrackOn({
            price: true,
            quote_volume: l.tracks?.includes('quote_volume') ?? true,
            open_interest: l.tracks?.includes('open_interest') ?? true,
            funding_rate: l.tracks?.includes('funding_rate') ?? true,
            liquidation: l.tracks?.includes('liquidation') ?? true,
          });
        }
      } catch { /* ignore */ }
    })();
  }, []);

  const mainH = 340;
  const subH = 120;

  // D06: 本地时区展示起止时间（仅改变显示，不改变样本集合）
  const fmtLocal = (t: number | undefined | null): string => {
    if (t == null || !isFinite(t)) return '—';
    return new Date(t).toLocaleString('zh-CN', { hour12: false, month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // R06/D10: 分位说明卡（桌面侧栏与窄屏下方共用同一内容）
  const pct = percentile?.percentile;
  const coverageOk = percentile?.quality === 'ok';
  const percentileCard = (
    <div className="card w-full !p-4">
      <div className="mb-2 flex items-center gap-1.5 text-sm font-bold">
        {zh ? '当前价格的历史位置' : 'Price position in history'}
        <MetricInfoPopover metricId="price" unit="A02">
          <span className="cursor-help text-gf-muted">ⓘ</span>
        </MetricInfoPopover>
      </div>
      <div className="text-[11px] text-gf-muted">
        {instrumentId} · {range} / {interval}
      </div>
      {pct != null && percentile ? (
        <div className="mt-3">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-2xl font-bold text-gf-accent">
              {pct.toFixed(1)} {zh ? '百分位' : 'percentile'}
            </span>
            <span className="text-right text-[11px] leading-tight text-gf-muted">
              <span className="block">P{pct.toFixed(1)}</span>
              <span className="block">{percentile.current != null ? fmtVal('price', 'price', percentile.current) : '—'}</span>
            </span>
          </div>
          {/* D10: 用户可读解释 */}
          <div className="mt-2 text-[11px] leading-relaxed text-gf-muted">
            {zh
              ? `在所选 ${range}、${interval} 收盘价样本中，约 ${pct.toFixed(1)}% 的有效样本价格不高于当前价格。仅表示这段历史中的相对位置，不是上涨概率、回报率或胜率。`
              : `About ${pct.toFixed(1)}% of valid ${range}/${interval} close samples are not above the current price. A relative position only — not a win rate or return forecast.`}
          </div>
          {/* 0/25/50/75/100 刻度 */}
          <div className="relative mt-3 h-2 overflow-hidden rounded-full bg-gf-border/40">
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-gf-accent opacity-85"
              style={{ width: `${Math.max(2, Math.min(100, pct))}%` }}
            />
            <div
              className="absolute inset-y-[-2px] w-[2px] rounded bg-gf-text"
              style={{ left: `calc(${Math.max(0, Math.min(100, pct))}% - 1px)` }}
            />
          </div>
          <div className="mt-0.5 flex justify-between text-[9px] text-gf-muted">
            {['0', '25', '50', '75', '100'].map((q) => <span key={q}>{q}</span>)}
          </div>
          <div className="mt-2 space-y-1 text-[11px] text-gf-muted">
            <div className="flex justify-between"><span>{zh ? '样本最低收盘价' : 'Sample min close'}</span><span className="font-mono text-gf-text">{percentile.min != null ? fmtVal('price', 'price', percentile.min) : '—'}</span></div>
            <div className="flex justify-between"><span>{zh ? '样本中位收盘价' : 'Sample median close'}</span><span className="font-mono text-gf-text">{percentile.median != null ? fmtVal('price', 'price', percentile.median) : '—'}</span></div>
            <div className="flex justify-between"><span>{zh ? '样本最高收盘价' : 'Sample max close'}</span><span className="font-mono text-gf-text">{percentile.max != null ? fmtVal('price', 'price', percentile.max) : '—'}</span></div>
            <div className="flex justify-between">
              <span>{zh ? '数据覆盖' : 'Coverage'}</span>
              <span className={coverageOk ? 'text-gf-positive' : 'text-gf-accent'}>
                {zh
                  ? (coverageOk ? `有效 ${percentile.samples}/应有 ${percentile.expected} · ${(percentile.coverage * 100).toFixed(1)}%` : `样本覆盖不足 · 有效 ${percentile.samples}/应有 ${percentile.expected} · ${(percentile.coverage * 100).toFixed(1)}%`)
                  : (coverageOk ? `${percentile.samples}/${percentile.expected} valid · ${(percentile.coverage * 100).toFixed(1)}%` : `Insufficient coverage · ${percentile.samples}/${percentile.expected} valid · ${(percentile.coverage * 100).toFixed(1)}%`)}
              </span>
            </div>
          </div>
          {/* D06/D10: 信息弹层 — 可复核的具体口径（点击展开，手机同样可用） */}
          <div className="mt-2 border-t border-gf-border/50 pt-2">
            <button
              onClick={() => setInfoOpen((v) => !v)}
              className="text-[10px] text-gf-accent hover:underline"
            >
              {zh ? (infoOpen ? '收起口径详情 ▴' : '口径详情 ▾') : (infoOpen ? 'Collapse details ▴' : 'Window details ▾')}
            </button>
            {infoOpen && (
              <div className="mt-2 space-y-1 text-[10px] leading-relaxed text-gf-muted">
                <div>{zh ? '统计窗口（本地时区）' : 'Window (local time)'}：{fmtLocal(percentile.windowStart)} → {fmtLocal(percentile.windowEnd)}</div>
                <div>{zh ? '采样字段' : 'Sample field'}：{interval} {zh ? '收盘价' : 'close'}（{zh ? '已收盘、有效、去重' : 'closed, valid, deduped'}）</div>
                <div>{zh ? '有效样本' : 'Valid samples'}：{percentile.samples} / {zh ? '应有' : 'expected'} {percentile.expected}</div>
                <div>{zh ? '计算方式' : 'Formula'}：{zh ? '不高于当前价格的样本数 ÷ 有效样本数 × 100（相等值计入）' : 'count(≤ current) ÷ validCount × 100, equal values counted in'}</div>
                <div>{zh ? '更新时间' : 'Updated'}：{fmtLocal(percentile.asOf)}</div>
                <div>{zh ? '现价为待比较值，未计入样本' : 'Current price is the comparison target, excluded from samples'}</div>
                <div className="pt-1">
                  {zh
                    ? '说明：分位只描述当前价格在该历史窗口中的相对位置，不是上涨概率、回报率或胜率；低分位不等于低估或买点，高分位不等于卖点。主图滚轮可缩放，拖拽可平移，十字光标在所有轨道同步。'
                    : 'Percentile only describes where the current price sits within this history window — not a win rate, return forecast, or buy/sell signal. Scroll to zoom, drag to pan; crosshair syncs across all tracks.'}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-3 text-[11px] leading-relaxed text-gf-muted">
          {percentile?.samples != null && percentile.samples > 0
            ? (zh
              ? `样本覆盖不足：有效 ${percentile.samples}/应有 ${percentile.expected}（${((percentile.coverage ?? 0) * 100).toFixed(1)}%）。不足 30 个有效样本或覆盖低于 80% 时不输出位置判断。`
              : `Insufficient samples: ${percentile.samples}/${percentile.expected} valid (${((percentile.coverage ?? 0) * 100).toFixed(1)}%). Position is not emitted below 30 valid samples or under 80% coverage.`)
            : (zh
              ? '暂无可比较数据：该标的此区间/粒度没有有效历史样本。'
              : 'No comparable data: no valid samples for this instrument/range/interval.')}
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div className="mb-4">
        <div>
          <h1 className="text-2xl font-bold">{zh ? '联动分析' : 'Linked Analysis'}</h1>
          <p className="mt-0.5 text-xs text-gf-muted">
            {zh ? '价格、成交量、持仓、费率、爆仓共享时间轴与十字光标' : 'Price, volume, OI, funding & liq share one timeline & crosshair'}
          </p>
        </div>
        {/* 筛选区：标题下方独立一行（币对/交易所/类型/周期/粒度/保存布局） */}
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
          {/* 币种：关键词联想搜索 */}
          <SymbolPicker
            value={symbol}
            zh={zh}
            onPick={({ symbol: s, exchange, type, baseAsset, quoteAsset }) => {
              setSymbol(s);
              if (baseAsset) setQuote(quoteAsset || 'USDT');
              if (exchange) {
                const ex = exchange.toLowerCase();
                if (VENUES.some((v) => v.id === ex)) setVenue(ex);
              }
              if (type) setProduct(type === 'spot' ? 'spot' : 'perp');
              setUnsaved(true);
            }}
          />
          <select value={venue} onChange={(e) => { setVenue(e.target.value); setUnsaved(true); }} className="input !w-[96px] !py-1 text-xs">
            {VENUES.map((v) => (
              <option key={v.id} value={v.id}>{v.label}</option>
            ))}
          </select>
          <div className="flex overflow-hidden rounded border border-gf-border">
            {(['perp', 'spot'] as const).map((p) => (
              <button
                key={p}
                onClick={() => { setProduct(p); setUnsaved(true); }}
                className={`px-2 py-1 ${product === p ? 'bg-gf-accent/20 text-gf-accent' : 'text-gf-muted'}`}
              >
                {p === 'perp' ? (zh ? '永续' : 'Perp') : (zh ? '现货' : 'Spot')}
              </button>
            ))}
          </div>
          <select value={range} onChange={(e) => { setRange(e.target.value); setUnsaved(true); }} className="input !w-[64px] !py-1 text-xs">
            {RANGES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <select value={interval} onChange={(e) => { setInterval(e.target.value); setUnsaved(true); }} className="input !w-[64px] !py-1 text-xs">
            {INTERVALS.map((i) => (
              <option key={i} value={i}>{i}</option>
            ))}
          </select>
          <button
            className="btn !py-1.5 text-xs"
            onClick={() => { setLayoutOpen((v) => !v); if (user) loadLayouts(); }}
          >
            {zh ? '保存布局' : 'Layout'}
          </button>
        </div>
      </div>

      {/* 布局面板 */}
      {layoutOpen && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-gf-accent/30 bg-gf-surface2/60 p-2.5">
          <input
            value={layoutName}
            onChange={(e) => setLayoutName(e.target.value)}
            placeholder={zh ? '布局名称（≤40 字符）' : 'Layout name (≤40 chars)'}
            maxLength={40}
            className="input !w-52 !py-1.5 text-xs"
          />
          <button className="btn-primary !py-1.5 text-xs" onClick={saveLayout} disabled={saving}>
            {saving ? '…' : zh ? '保存' : 'Save'}
          </button>
          {layoutSaved && <span className="text-[11px] text-gf-positive">{zh ? '已保存 ✓' : 'Saved ✓'}</span>}
          {activeLayoutId && (
            <button className="btn !py-1.5 text-xs" onClick={() => shareLayout(activeLayoutId)}>
              {zh ? '分享' : 'Share'}
            </button>
          )}
          {shareUrl && (
            <span className="flex min-w-0 flex-1 items-center gap-1.5 text-[11px]">
              <input readOnly value={shareUrl} onFocus={(e) => e.target.select()} className="input min-w-0 flex-1 !py-1 text-[10px]" />
              <button className="shrink-0 text-[10px] text-gf-accent" onClick={() => { navigator.clipboard?.writeText(shareUrl); }}>
                {zh ? '复制' : 'Copy'}
              </button>
            </span>
          )}
          {/* 我的布局列表 */}
          {user && layouts.length > 0 && (
            <div className="flex w-full flex-wrap gap-1.5 border-t border-gf-border/50 pt-2">
              {layouts.map((l) => (
                <span key={l.id} className="inline-flex items-center gap-1 rounded-full border border-gf-border bg-gf-bg/40 px-2 py-0.5 text-[10px]">
                  <button className="text-gf-muted hover:text-gf-accent" onClick={() => applyLayout(l)}>
                    {l.name} <span className="text-gf-muted/60">v{l.version}</span>
                  </button>
                  <button className="text-gf-muted/60 hover:text-gf-accent" onClick={() => shareLayout(l.id)} title={zh ? '分享' : 'Share'}>↗</button>
                  <button className="text-gf-muted/60 hover:text-gf-negative" onClick={() => deleteLayout(l.id)} title={zh ? '删除' : 'Delete'}>×</button>
                </span>
              ))}
            </div>
          )}
          {!user && (
            <span className="text-[11px] text-gf-muted">
              {language === 'zh-Hant' ? '登入後可保存佈局' : zh ? '登录后可保存布局' : 'Log in to save layouts'}
            </span>
          )}
        </div>
      )}

      <div className="flex items-start gap-4">
        {/* 主区图表栈 */}
        <div className="min-w-0 flex-1">
          <div className="relative">
            {stale && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-gf-bg/60 backdrop-blur-[1px]">
                <span className="rounded-full border border-gf-accent/40 bg-gf-accent/10 px-3 py-1 text-xs text-gf-accent">
                  {zh ? '正在更新…' : 'Updating…'}
                </span>
              </div>
            )}
            {loading && !tracks ? (
              <div className="card p-10 text-center text-xs text-gf-muted">{t.common.loading}…</div>
            ) : error ? (
              <div className="card p-10 text-center text-xs text-gf-negative">
                {zh ? '载入失败，请检查标的与参数' : 'Failed to load — check symbol & params'}
              </div>
            ) : tracks && tracks.length ? (
              <>
                {/* 桌面：完整栈 */}
                <div className="hidden sm:block">
                  <LinkedChart tracks={tracks} height={mainH} subHeight={subH} windowStart={seriesMeta?.windowStart} windowEnd={seriesMeta?.windowEnd} expectedCount={seriesMeta?.expectedCount} />
                </div>
                {/* 手机：主图 + 单副图切换 */}
                <div className="sm:hidden">
                  <LinkedChart
                    tracks={tracks.filter((tr) => tr.metricId === 'price' || tr.metricId === 'quote_volume' || tr.metricId === mobileSub)}
                    height={280}
                    subHeight={180}
                    windowStart={seriesMeta?.windowStart}
                    windowEnd={seriesMeta?.windowEnd}
                    expectedCount={seriesMeta?.expectedCount}
                  />
                  <div className="mt-2 grid grid-cols-3 gap-1.5">
                    {(['open_interest', 'funding_rate', 'liquidation'] as const)
                      .filter((m) => product === 'perp' || m === 'liquidation')
                      .map((m) => (
                        <button
                          key={m}
                          onClick={() => setMobileSub(m)}
                          className={`rounded-lg border px-2 py-1.5 text-[11px] ${
                            mobileSub === m ? 'border-gf-accent/50 bg-gf-accent/15 text-gf-accent' : 'border-gf-border bg-gf-surface2 text-gf-muted'
                          }`}
                        >
                          {m === 'open_interest' ? (zh ? '持仓' : 'OI') : m === 'funding_rate' ? (zh ? '费率' : 'Funding') : (zh ? '爆仓' : 'Liq')}
                        </button>
                      ))}
                  </div>
                </div>
                {/* 轨道开关（桌面）— R06: every track is user-configurable */}
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span className="text-[10px] text-gf-muted">{zh ? '轨道' : 'Tracks'}:</span>
                  {availableTracks.map((mt) => (
                    <button
                      key={mt}
                      onClick={() => setTrackOn((v) => ({ ...v, [mt]: !v[mt] }))}
                      disabled={mt === 'price'}
                      title={mt === 'price' ? (zh ? '价格为主图，不可关闭' : 'Price is the main chart — always on') : undefined}
                      className={`rounded-full border px-2 py-0.5 text-[10px] ${
                        mt === 'price'
                          ? 'border-gf-border bg-gf-surface2 text-gf-muted opacity-50'
                          : trackOn[mt]
                            ? 'border-gf-accent/50 bg-gf-accent/15 text-gf-accent'
                            : 'border-gf-border bg-gf-surface2 text-gf-muted'
                      }`}
                    >
                      {mt === 'price' ? (zh ? '价格' : 'Price') : mt === 'quote_volume' ? (zh ? '成交量' : 'Volume') : mt === 'open_interest' ? (zh ? '持仓' : 'OI') : mt === 'funding_rate' ? (zh ? '费率' : 'Funding') : (zh ? '爆仓' : 'Liq')}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="card p-10 text-center text-xs text-gf-muted">{zh ? '暂无数据' : 'No data'}</div>
            )}
          </div>
        </div>

        {/* 分位说明栏：桌面右侧 320px；视口不足时移到图表下方（R06） */}
        <div className="hidden w-[320px] shrink-0 xl:block">{percentileCard}</div>
      </div>

      {/* 视口不足时的分位卡（xl 以下，位于图表栈之后） */}
      <div className="mt-3 xl:hidden">{percentileCard}</div>
    </div>
  );
}
